import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/Button'

/**
 * What a page shows when it throws.
 *
 * Without one of these the router prints its own developer notice — the stack, and a line
 * addressed to whoever wrote the app. That is the right page for nobody: it tells a user nothing
 * they can act on, and it is what they photograph and send instead of what happened.
 *
 * So: what went wrong in one line, the two things worth trying, and the message itself kept in
 * small print rather than hidden, because it is the one detail a support conversation needs.
 */
export function RouteErrorBoundary() {
  const error = useRouteError()
  const navigate = useNavigate()

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm">
        <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-amber-50">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
        </span>
        <h1 className="mt-4 text-base font-semibold text-gray-900">{headline(error)}</h1>
        <p className="mt-2 text-sm text-gray-500">
          The page did not load. Trying again usually settles it — if it does not, send us the line
          below and we will look into it.
        </p>
        <p className="mt-4 break-words rounded border border-gray-100 bg-gray-50 px-3 py-2 text-xs text-gray-500">
          {detail(error)}
        </p>
        <div className="mt-6 flex justify-center gap-2">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Go back
          </Button>
          <Button type="button" onClick={() => window.location.reload()}>
            Try again
          </Button>
        </div>
      </div>
    </div>
  )
}

function headline(error: unknown): string {
  if (isRouteErrorResponse(error)) {
    return error.status === 404 ? 'Page not found' : `Something went wrong (${error.status})`
  }
  return 'Something went wrong'
}

function detail(error: unknown): string {
  if (isRouteErrorResponse(error)) return error.statusText || String(error.status)
  if (error instanceof Error) return error.message
  return String(error)
}
